import React, { useEffect, useState } from "react";
import { apiClient } from "../../../api/apiClient";
import "../../../assets/css/admin/cc/CcIntroAdmin.css";
import "../../../assets/css/admin/AdminCommon.css";
import flowerIcon from "../../../assets/img/flower_icon.png";
import { getStoredAdminInfo } from "../AdminAuth";

const tabToCouncilType = {
  student: "STUDENT_COUNCIL",
  emergency: "EMERGENCY_COMMITTEE",
  proxy: "ACTING",
};

const defaultForm = {
  name: "",
  slogan: "",
  imageFile: null,
  imageName: "",
  imageUrl: "",
  officeLocation: "",
  kakaoUrl: "",
  instagramUrl: "",
};

export default function CcIntroAdmin() {
  const [activeTab, setActiveTab] = useState("student");
  const [formData, setFormData] = useState(defaultForm);
  const [loading, setLoading] = useState(false);

  const { isAudit, unitName, collegeId } = getStoredAdminInfo();

  const currentCouncilType = isAudit
    ? "STUDENT_COUNCIL"
    : tabToCouncilType[activeTab];

  const getNamePlaceholder = () => {
    if (isAudit) {
      return "이름을 입력해 주세요.";
    }

    if (activeTab === "student") {
      return "제00대 단과대명 학생회 학생회명 형식으로 작성해 주세요.";
    }

    if (activeTab === "emergency") {
      return "제00대 단과대명 비상대책위원회 형식으로 작성해 주세요.";
    }

    return "이름을 입력해 주세요.";
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!collegeId) {
        alert("소속 단위 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
        setFormData(defaultForm);
        return;
      }

      try {
        setLoading(true);

        const result = await apiClient(`/api/colleges/${collegeId}/profile`);
        const data = result?.data;

        if (!data) {
          setFormData(defaultForm);

          if (isAudit) {
            setActiveTab("student");
          }

          return;
        }

        setFormData({
          name: data.name || "",
          slogan: data.slogan || "",
          imageFile: null,
          imageName: data.profileImg ? "기존 대표 이미지" : "",
          imageUrl: data.profileImg || "",
          officeLocation: data.officeLocation || "",
          kakaoUrl: data.kakaoUrl || "",
          instagramUrl: data.instagramUrl || "",
        });

        if (isAudit) {
          setActiveTab("student");
          return;
        }

        if (data.type === "EMERGENCY_COMMITTEE") {
          setActiveTab("emergency");
        } else if (data.type === "ACTING") {
          setActiveTab("proxy");
        } else {
          setActiveTab("student");
        }
      } catch (error) {
        console.error("단위 프로필 조회 오류:", error);
        setFormData(defaultForm);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [collegeId, isAudit]);

  const handleTabChange = (nextTab) => {
    if (isAudit) return;

    setActiveTab(nextTab);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      imageFile: file,
      imageName: file.name,
    }));

    e.target.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!collegeId) {
      alert("소속 단위 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
      return;
    }

    if (!isAudit && activeTab !== "proxy" && !formData.name.trim()) {
      alert("이름을 입력해 주세요.");
      return;
    }

    if (isAudit && !formData.name.trim()) {
      alert("이름을 입력해 주세요.");
      return;
    }

    try {
      setLoading(true);

      const requestFormData = new FormData();

      requestFormData.append("type", currentCouncilType);

      requestFormData.append(
        "name",
        !isAudit && activeTab === "proxy" ? "" : formData.name
      );

      requestFormData.append(
        "slogan",
        !isAudit && activeTab === "student" ? formData.slogan : ""
      );

      requestFormData.append(
        "officeLocation",
        !isAudit && activeTab === "proxy" ? "" : formData.officeLocation
      );

      requestFormData.append(
        "kakaoUrl",
        !isAudit && activeTab === "proxy" ? "" : formData.kakaoUrl
      );

      requestFormData.append(
        "instagramUrl",
        !isAudit && activeTab === "proxy" ? "" : formData.instagramUrl
      );

      if ((isAudit || activeTab === "student") && formData.imageFile) {
        requestFormData.append("profileImg", formData.imageFile);
      }

      await apiClient(`/api/colleges/${collegeId}/profile`, {
        method: "PUT",
        body: requestFormData,
      });

      alert("저장되었습니다.");
    } catch (error) {
      console.error("단위 프로필 저장 오류:", error);
      alert(error.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cc-intro-shell">
      <div className="cc-intro-page admin-scroll-x">
        <form onSubmit={handleSubmit} className="cc-intro-form">
          <div className="cc-intro-header">
            <h2>{unitName}</h2>

            {!isAudit && (
              <div className="cc-tab-box">
                <button
                  type="button"
                  className={activeTab === "student" ? "active" : ""}
                  onClick={() => handleTabChange("student")}
                >
                  학생회
                </button>

                <button
                  type="button"
                  className={activeTab === "emergency" ? "active" : ""}
                  onClick={() => handleTabChange("emergency")}
                >
                  비상대책위원회
                </button>

                <button
                  type="button"
                  className={activeTab === "proxy" ? "active" : ""}
                  onClick={() => handleTabChange("proxy")}
                >
                  권한대행
                </button>
              </div>
            )}
          </div>

          {!isAudit && activeTab === "proxy" ? (
            <div className="cc-intro-content">
              <div className="cc-empty-state">현재 권한대행 상태입니다.</div>
            </div>
          ) : (
            <div className="cc-intro-content">
              <div className="cc-form-row">
                <label>
                  <img src={flowerIcon} alt="" />
                  이름
                </label>

                <input
                  type="text"
                  placeholder={getNamePlaceholder()}
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </div>

              {!isAudit && activeTab === "student" && (
                <div className="cc-form-row">
                  <label>
                    <img src={flowerIcon} alt="" />
                    구호
                  </label>

                  <input
                    type="text"
                    placeholder="구호를 입력해 주세요."
                    value={formData.slogan}
                    onChange={(e) => handleChange("slogan", e.target.value)}
                  />
                </div>
              )}

              {(isAudit || activeTab === "student") && (
                <div className="cc-form-row">
                  <label>
                    <img src={flowerIcon} alt="" />
                    대표 이미지
                  </label>

                  <div className="cc-file-input-group">
                    <input
                      type="text"
                      placeholder="대표 이미지를 업로드해 주세요."
                      value={formData.imageName}
                      readOnly
                    />

                    <label className="cc-file-btn">
                      파일 선택
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>
                </div>
              )}

              <div className="cc-form-row">
                <label>
                  <img src={flowerIcon} alt="" />
                  {isAudit ? "위치" : "학생회실 위치"}
                </label>

                <input
                  type="text"
                  placeholder={
                    isAudit
                      ? "위치를 입력해 주세요."
                      : "학생회실 위치를 입력해 주세요."
                  }
                  value={formData.officeLocation}
                  onChange={(e) =>
                    handleChange("officeLocation", e.target.value)
                  }
                />
              </div>

              <div className="cc-form-row">
                <label>
                  <img src={flowerIcon} alt="" />
                  카카오톡
                </label>

                <input
                  type="text"
                  placeholder="링크를 입력해 주세요."
                  value={formData.kakaoUrl}
                  onChange={(e) => handleChange("kakaoUrl", e.target.value)}
                />
              </div>

              <div className="cc-form-row">
                <label>
                  <img src={flowerIcon} alt="" />
                  인스타그램
                </label>

                <input
                  type="text"
                  placeholder="링크를 입력해 주세요."
                  value={formData.instagramUrl}
                  onChange={(e) =>
                    handleChange("instagramUrl", e.target.value)
                  }
                />
              </div>
            </div>
          )}

          <div className="cc-save-wrap">
            <button
              type="submit"
              className="cc-save-btn"
              disabled={loading || !collegeId}
            >
              {loading ? "저장 중..." : "저장"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}